import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import Header from './components/Header';
import BottomBar from './components/BottomBar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import CreateProduct from './pages/CreateProduct';
import ProductDetail from './pages/ProductDetail';
import MyProducts from './pages/MyProducts';
import BulkImportProducts from './pages/BulkImportProducts';
import Favorites from './pages/Favorites';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminProducts from './pages/AdminProducts';
import AdminTheme from './pages/AdminTheme';
import AdminMessages from './pages/AdminMessages';
import UserDashboard from './pages/UserDashboard';
import EditProduct from './pages/EditProduct';
import NotFound from './pages/NotFound';
import Contact from './pages/Contact';
import MentionsLegales from './pages/MentionsLegales';
import CGV from './pages/CGV';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import MyMessages from './pages/MyMessages';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CompleteRegistration from './pages/CompleteRegistration';
import VerifyEmail from './pages/VerifyEmail';
import SellerOnboarding from './pages/SellerOnboarding';
import BuyProduct from './pages/BuyProduct';
import PaymentSuccess from './pages/PaymentSuccess';
import Cart from './pages/Cart';
import Wallet from './pages/Wallet';
import MyPurchases from './pages/MyPurchases';
import Mouvements from './pages/Mouvements';
import AdminOrders from './pages/AdminOrders';
import AdminConversations from './pages/AdminConversations';
import AdminNotifications from './pages/AdminNotifications';
import UserNotifications from './pages/UserNotifications';
import InstallPrompt from './components/InstallPrompt';
import './styles/App.css';

// Composant pour protéger les routes
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
};

// Composant pour protéger les routes admin
const AdminRoute = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const pageTitleKeys = {
  '/': 'home',
  '/login': 'login',
  '/register': 'register',
  '/contact': 'contact',
  '/dashboard': 'dashboard',
  '/create-product': 'createProduct',
  '/my-products': 'myProducts',
  '/mouvements': 'movements',
  '/favorites': 'favorites',
  '/my-messages': 'messages',
  '/seller/onboarding': 'sellerOnboarding',
  '/cart': 'cart',
  '/notifications': 'notifications',
  '/admin': 'admin',
  '/admin/conversations': 'adminConversations',
  '/admin/notifications': 'adminNotifications',
  // Pages publiques indexees : le libelle doit rester IDENTIQUE a celui que
  // backend/server.js (PAGE_META) ecrit dans le HTML envoye. Un titre qui
  // change entre le HTML brut et le HTML affiche est un signal contradictoire
  // pour Google.
  '/cgv': 'cgv',
  '/mentions-legales': 'mentionsLegales',
  '/politique-de-confidentialite': 'confidentialite',
  '/vente-occasion-suisse': 'venteOccasionSuisse',
  '/seconde-main-suisse': 'secondeMainSuisse',
  '/marketplace-suisse': 'marketplaceSuisse',
  '/petites-annonces-suisse': 'petitesAnnoncesSuisse',
  '/vente-en-ligne-suisse': 'venteEnLigneSuisse',
  '/bonnes-affaires-suisse': 'bonnesAffairesSuisse',
};

/*
 * Les pages dont le titre vient de leur CONTENU, pas d'une table : la fiche
 * d'une annonce s'intitule du nom de l'annonce. Elles le posent elles-memes
 * (voir ProductDetail.js) et PageTitle ne repasse pas derriere.
 */
const CHEMINS_A_TITRE_PROPRE = [/^\/product\//];

/*
 * Le titre ecrit par le serveur dans le HTML d'arrivee, lu AVANT que React ne
 * rende quoi que ce soit.
 *
 * Il etait jusqu'ici ecrase sans condition : `pageTitleKeys` ne contenant que
 * des chemins fixes, toute fiche produit retombait sur `pageTitles.default`,
 * et les quarante-sept annonces du site partageaient le titre « Main2Main -
 * Marketplace ». Le serveur en fabriquait pourtant un bon pour chacune, et
 * Google indexe le DOM affiche : le travail etait annule au montage.
 */
const titreServeur = typeof document !== 'undefined' ? document.title : '';
const cheminServeur = typeof window !== 'undefined' ? window.location.pathname : '';

const PageTitle = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language || 'fr';

    if (CHEMINS_A_TITRE_PROPRE.some((motif) => motif.test(location.pathname))) return;

    /*
     * Sur l'URL d'arrivee, le titre du serveur PASSE DEVANT la table : c'est
     * celui que le robot a lu dans le HTML brut, et le garder tel quel evite
     * qu'il lise autre chose dans le HTML affiche. La table reprend la main
     * des la premiere navigation interne, ou plus aucun robot ne nous suit.
     *
     * Consequence assumee : changer de langue en restant sur la page d'arrivee
     * ne retraduit pas l'onglet. Il se retraduit a la navigation suivante.
     */
    if (location.pathname === cheminServeur && titreServeur) {
      document.title = titreServeur;
      return;
    }

    const key = pageTitleKeys[location.pathname];
    document.title = key ? t(`pageTitles.${key}`) : t('pageTitles.default');
  }, [location.pathname, t, i18n.language]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
      <Router>
        <div className="App">
          <Header />
          <main className="main-content">
            <Routes>
              {/* Routes publiques */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/complete-registration/:token" element={<CompleteRegistration />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/cgv" element={<CGV />} />
              <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />

              {/* SEO Landing Pages - redirect to Home */}
              <Route path="/vente-occasion-suisse" element={<Home />} />
              <Route path="/seconde-main-suisse" element={<Home />} />
              <Route path="/marketplace-suisse" element={<Home />} />
              <Route path="/petites-annonces-suisse" element={<Home />} />
              <Route path="/vente-en-ligne-suisse" element={<Home />} />
              <Route path="/bonnes-affaires-suisse" element={<Home />} />

              {/* Routes utilisateur */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <UserDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-product"
                element={
                  <ProtectedRoute>
                    <CreateProduct />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-vehicle"
                element={
                  <ProtectedRoute>
                    <CreateProduct vehicleMode />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-product/:id"
                element={
                  <ProtectedRoute>
                    <EditProduct />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-products"
                element={
                  <ProtectedRoute>
                    <MyProducts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-import"
                element={
                  <ProtectedRoute>
                    <BulkImportProducts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/favorites"
                element={
                  <ProtectedRoute>
                    <Favorites />
                  </ProtectedRoute>
                }
              />

              {/* Messages */}
              <Route
                path="/my-messages"
                element={
                  <ProtectedRoute>
                    <MyMessages />
                  </ProtectedRoute>
                }
              />

              {/* Notifications utilisateur */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <UserNotifications />
                  </ProtectedRoute>
                }
              />

              {/* Marketplace - Vendeur */}
              <Route
                path="/seller/onboarding"
                element={
                  <ProtectedRoute>
                    <SellerOnboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/seller/onboarding/refresh"
                element={
                  <ProtectedRoute>
                    <SellerOnboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/seller/onboarding/complete"
                element={
                  <ProtectedRoute>
                    <SellerOnboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wallet"
                element={
                  <ProtectedRoute>
                    <Wallet />
                  </ProtectedRoute>
                }
              />

              {/* Marketplace - Acheteur */}
              <Route
                path="/buy/:id"
                element={
                  <ProtectedRoute>
                    <BuyProduct />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cart"
                element={
                  <ProtectedRoute>
                    <Cart />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-success"
                element={
                  <ProtectedRoute>
                    <PaymentSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-purchases"
                element={
                  <ProtectedRoute>
                    <MyPurchases />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mouvements"
                element={
                  <ProtectedRoute>
                    <Mouvements />
                  </ProtectedRoute>
                }
              />

              {/* Routes admin */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <AdminUsers />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/products"
                element={
                  <AdminRoute>
                    <AdminProducts />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/theme"
                element={
                  <AdminRoute>
                    <AdminTheme />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/orders"
                element={
                  <AdminRoute>
                    <AdminOrders />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/conversations"
                element={
                  <AdminRoute>
                    <AdminConversations />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/messages"
                element={
                  <AdminRoute>
                    <AdminMessages />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <AdminRoute>
                    <AdminNotifications />
                  </AdminRoute>
                }
              />

              {/* Redirections pour compatibilité */}
              <Route path="/annonce/:id" element={<Navigate to="/product/:id" replace />} />
              <Route path="/create-annonce" element={<Navigate to="/create-product" replace />} />
              <Route path="/my-annonces" element={<Navigate to="/my-products" replace />} />

              {/* Route 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
          <BottomBar />
          <InstallPrompt />
          <PageTitle />
        </div>
      </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
